/**
 * Колонка «Исходник» экрана черновика: правка текста разметкой с живым разбором (Б5.4).
 *
 * ПЕРЕОПРЕДЕЛЕНИЕ НОРМЫ. `docs/phase-1/00-integration.md` §Окружение 12 откладывал редактор
 * исходника в фазу 2, и `docs/phase-1/05-ui.md` §5 прямо говорит «Фаза 1 — редактора нет».
 * Решение Ильи 24.08.2026 это отменяет: редактор фазы 1 строится целиком. Где §12 и 05-ui §5
 * расходятся с этим файлом — прав этот файл. Остальная часть §12 силы не теряет: функции
 * разбора живут в `@smm/contracts`, а не здесь.
 *
 * ЧТО ЗДЕСЬ ВАЖНО, по пунктам:
 *
 * 1. **Обычная textarea, не contenteditable** (решение 22.08, docs/ui-design.md §6.5). Смещения
 *    сущностей считаются в единицах UTF-16, и offset, попавший в середину суррогатной пары, —
 *    это жёсткая 400 от Telegram уже ПОСЛЕ одобрения поста. В варианте с разметкой границы
 *    сущностей возникают только на ASCII-символах маркеров, поэтому попасть в середину пары
 *    они не могут в принципе. В contenteditable границы ставит браузер, и гарантии нет.
 *
 * 2. **Поле заполняется обратной сериализацией.** Сервис генерации отдаёт `{text, entities}`,
 *    а не разметку, поэтому при открытии версии зовётся `entitiesToSource(model.canonical)`.
 *    Round-trip доказан property-тестом по всему корпусу фикстур — здесь он просто используется.
 *
 * 3. **Ни одного правила разметки и канонизации в этом файле.** Разбор — `sourceToEntities`,
 *    предсохранная проверка — `freezeAuthored` (та же функция, которой бэкенд собирает
 *    черновик из авторских частей), список блокирующих — `validateDraft`. Копия правил здесь
 *    стала бы четвёртой рядом с генератором, подписью и предотправочным валидатором
 *    (ПРАВИЛО 4.5 контракта), и разошлась бы первой — а расхождение означает 400 на уже
 *    утверждённом посте.
 *
 * 4. **Хэш содержимого не считается и не отправляется.** Его считает бэкенд из своей же
 *    канонической формы (`domain/human-edit.ts` §1). Второй источник правды о содержимом
 *    запрещён: «то, что подписали» и «то, что показали» не имеют права разойтись.
 *
 * 5. **Правка — это новая версия, а не перезапись.** Так и написано на кнопке и под ней.
 *    Кнопка появляется только при отличии от исходной версии; `link_preview_options` в теле
 *    нет вовсе — отсутствие ключа означает «карточка следует за текстом»: бэкенд сам ставит
 *    её на первую ссылку сохранённого текста (`domain/human-edit.ts` §6, обработчик в
 *    `listeners/ui.ts`).
 *
 * 6. **Живое превью соседней колонки живёт от СОХРАНЁННОЙ версии.** Поднять набранный текст
 *    туда `SourceEditorPanelProps` не позволяет — граница описана в `routes/contracts.ts` и
 *    расширять её самовольно нельзя. Поэтому колонка честно говорит, что правка не сохранена
 *    и что превью показывает v_N. Контракт, который для этого нужен от `DraftPanel`, вынесен
 *    в отчёт — менять чужой файл эта задача не даёт.
 *
 * 7. **Заморозка — с политикой бренда и с файлом версии** (19.09.2026, docs/release-2026-09-19.md
 *    §9.2–§9.3). Сервер сохранит правку `freezeAuthored(…, {richAlways})` по политике «все посты
 *    — расширенным сообщением» и с документом правимой версии (правка текста файл не снимает).
 *    Проверь редактор правку без них — он судил бы о форме, которой не будет: без документа
 *    короткий пост с файлом выглядел бы обычным сообщением, без политики — не проверялся бы
 *    правилом блоков rich (`rich_block_misaligned`), которым его проверит сервер.
 *
 * 8. **Организации, запрещённые в РФ, — та же разметка, что у сервера, и только показ**
 *    (build spec 2026-11-02 §3, флаг бренда `banned_orgs` из `GET /api/v1/brands`). Сервер
 *    ставит звёздочки и сноску при каждом сохранении (`prepare` приёма), поэтому предсохранная
 *    заморозка, счётчик, транспорт и живое превью считают набранное ПОСЛЕ `applyBannedOrgMarks`:
 *    сноска добавляет к посту восемнадцать кодпоинтов, и без неё редактор обещал бы карточку
 *    правке, которая уйдёт расширенным сообщением. Тело сохранения прежнее — текст, как набран;
 *    что поменяет сервер, сказано строкой под полем и в подтверждении (`routes/bannedOrgs.ts`).
 *    Версия, сохранённая до разметки, одобрения не пройдёт (`banned_org_unmarked`, гейт экрана
 *    поста), а правки в ней может не быть вовсе, — поэтому там, где стоит гейт, кнопка
 *    сохранения есть и без правки: «Сохранить с пометками» отправляет текст версии как есть, и
 *    сервер его размечает. У версии «на переделке» такой кнопки нет: её заменит версия писателя,
 *    уже размеченная, а сохранение человека закрыло бы идущую переделку.
 */

import {
  DRAFT_STATE_LABEL_RU,
  DRAFT_TRANSITIONS,
  type DraftState,
  entitiesToSource,
  freezeAuthored,
  type Issue,
  sourceToEntities,
} from '@smm/contracts';
import { applyBannedOrgMarks } from '@smm/contracts/preview';
import { TRANSPORT_RICH_ALWAYS_DEFAULT } from '@smm/contracts/schemas';
import { type CSSProperties, type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { brandBannedOrgs, useBrands } from '../../brand.tsx';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import {
  IconCheck,
  IconChevronRight,
  IconCode,
  IconEyeOff,
  IconKey,
  IconLink,
  IconQuote,
  IconWarn,
} from '../../components/icons.tsx';
import { group, LengthMeter } from '../../preview/LengthMeter.tsx';
import {
  cardOptionsOf,
  overLimit,
  REQUEST_BYTE_CAP,
  richReasonOf,
  SEND_MESSAGE_THRESHOLD,
  type TransportInput,
  validateDraft,
} from '../../preview/validation.ts';
import {
  bannedOrgGate,
  marksAfterSave,
  marksBeforeSave,
  marksChangeOf,
  marksNoVersion,
  unmarkedVersionText,
} from '../bannedOrgs.ts';
import type { SourceEditorPanelProps, UiIssue } from '../contracts.ts';
import { isStaleBaseVersion, saveErrorText, saveVersion } from '../versionSave.ts';
import styles from './SourceEditorPanel.module.css';
import {
  MARKERS,
  type SourceEdit,
  type SourceSelection,
  toggleQuote,
  wrapLink,
  wrapSelection,
} from './toolbar.ts';

/**
 * Состояния, из которых версия заменяется следующей. Не список литералом, а вывод из таблицы
 * переходов: набор `SUPERSEDABLE` бэкенда — это ровно `from` у рёбер в `superseded` (переходы
 * 5, 9, 12, 13). Литерал здесь был бы вторым списком, который разойдётся при первом же новом ребре.
 */
const EDITABLE: ReadonlySet<DraftState> = new Set(
  DRAFT_TRANSITIONS.filter((t) => t.to === 'superseded').map((t) => t.from),
);

/**
 * Коды, означающие «Telegram изменит то, что вы набрали». Берутся из предупреждений
 * `freezeAuthored` — то есть из того же прохода канонизации, который выполнит бэкенд.
 * Остальные предупреждения (голая ссылка, много сущностей, символы вне BMP) — про пост,
 * а не про набранный текст, и их место в колонке решения.
 */
const CHANGE_CODES: ReadonlySet<string> = new Set([
  'cr_removed',
  'trimmed_leading',
  'trimmed_trailing',
  'control_chars_replaced',
  'entity_dropped_by_trim',
]);

/** Общая фраза, которую Telegram-канонизация ставит рядом с подробностями. */
const CHANGE_GENERIC = 'text_modified_by_canonicalization';

interface Change {
  readonly code: string;
  readonly message: string;
}

/** Подтверждение сохранения помнит свой черновик — см. сброс состояния по ключу версии. */
interface Notice {
  readonly draftId: string;
  readonly text: string;
}

const issueToUi = (i: Issue): UiIssue => ({
  level: 'blocking',
  code: i.code,
  message: i.path === undefined ? i.message : `${i.message} (${i.path})`,
});

export function SourceEditorPanel({
  model,
  onChanged,
  onTypedChange,
  richAlways,
}: SourceEditorPanelProps): JSX.Element {
  const canonical = model.canonical;
  // Политика неизвестна — умолчание, то же, что у бэкенда без строки (`transport_policy`).
  const policy = richAlways ?? TRANSPORT_RICH_ALWAYS_DEFAULT;
  // Разметка организаций, запрещённых в РФ (пункт 8 шапки). Бренд — версии, а не адреса.
  const bannedOrgs = brandBannedOrgs(useBrands(), model.brand);
  const document = canonical.document ?? null;
  const baseline = useMemo(() => entitiesToSource(canonical), [canonical]);
  // Ключ версии: `content_hash` меняется после сохранения, поэтому после refetch поле
  // перезаполняется новой версией, а не остаётся с текстом предыдущей.
  const key = `${model.draft_id}:${String(model.version_no)}:${model.content_hash}`;

  const [loaded, setLoaded] = useState(key);
  const [source, setSource] = useState(baseline);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const areaRef = useRef<HTMLTextAreaElement>(null);
  const pendingSelection = useRef<SourceSelection | null>(null);

  // Правка состояния во время рендера, а не в эффекте: иначе один кадр показывает текст
  // предыдущей версии в поле уже открытой новой.
  //
  // Подтверждение сохранения ключ версии ПЕРЕЖИВАЕТ намеренно: удачное сохранение само меняет
  // версию, и сброс по ключу гасил бы единственную фразу, подтверждающую, что правка легла.
  // Гасит его переход на другой черновик и любая следующая правка.
  if (loaded !== key) {
    setLoaded(key);
    setSource(baseline);
    setError(null);
    if (notice !== null && notice.draftId !== model.draft_id) setNotice(null);
  }

  // Выделение после кнопки панели восстанавливается здесь: React перерисовывает textarea по
  // значению, и позиция каретки, выставленная до перерисовки, потерялась бы.
  useEffect(() => {
    const el = areaRef.current;
    const sel = pendingSelection.current;
    if (el === null || sel === null) return;
    pendingSelection.current = null;
    el.focus();
    el.setSelectionRange(sel.start, sel.end);
  });

  const parsed = useMemo(() => sourceToEntities(source), [source]);

  /**
   * Набранное так, как его сохранит сервер: у бренда с разметкой — со звёздочками и сноской
   * (пункт 8 шапки), у остальных — как набрано. `null` — разметка не разобрана. Отсюда считают
   * заморозка, счётчик и живое превью; на сервер уходит `parsed`.
   */
  const authored = useMemo(() => {
    if (!parsed.ok) return null;
    const typed = { text: parsed.text, entities: parsed.entities };
    return bannedOrgs ? applyBannedOrgMarks(typed) : typed;
  }, [parsed, bannedOrgs]);

  /** Что разметка сервера поменяет в набранном; `null` — ничего (пункт 8 шапки). */
  const marksChange = useMemo(
    () =>
      authored === null || !parsed.ok
        ? null
        : marksChangeOf({ text: parsed.text, entities: parsed.entities }, authored),
    [authored, parsed],
  );

  // Та же проверка, что выполнит бэкенд в `prepare()`: канонизация плюс лимиты. Не бросает.
  //
  // Метод НЕ передаётся, а ВЫВОДИТСЯ — это и есть работа `freezeAuthored` (план 09 §4).
  // Отдай мы прежний `method` от правимой версии, правка, переросшая порог, получила бы
  // `text_too_long` и выключенное сохранение при том, что сервер её принимает; а
  // rich-по-длине пост, сокращённый обратно, получил бы `method_not_derived`. Порог считается
  // по КАНОНИЧЕСКОМУ тексту, и это делает `freezeAuthored` сама: правка в 4070 знаков с
  // хвостовыми пробелами — это 4060 после канонизации, то есть обычное сообщение с карточкой.
  const checked = useMemo(() => {
    if (authored === null) return null;
    return freezeAuthored(
      {
        text: authored.text,
        entities: [...authored.entities],
        link_preview_options: cardOptionsOf(canonical.media, authored.entities),
        media: canonical.media,
        document,
      },
      { richAlways: policy },
    );
  }, [authored, canonical, document, policy]);

  const blocking: readonly UiIssue[] = useMemo(() => {
    if (checked === null) return [];
    if (!checked.ok) return checked.issues.filter((i) => i.severity === 'fatal').map(issueToUi);
    return validateDraft(checked.canonical, { richAlways: policy }).filter(
      (i) => i.level === 'blocking',
    );
  }, [checked, policy]);

  const changes: readonly Change[] = useMemo(() => {
    if (checked === null || !checked.ok) return [];
    const out: Change[] = checked.warnings
      .filter((w) => CHANGE_CODES.has(w.code))
      .map((w) => ({ code: w.code, message: w.message }));
    if (out.length === 0 && checked.warnings.some((w) => w.code === CHANGE_GENERIC)) {
      out.push({ code: CHANGE_GENERIC, message: 'Telegram изменит текст при сохранении.' });
    }
    // Сравнение — с текстом ПОСЛЕ разметки сервера: курсив сноски — её сущность, а не работа
    // канонизации, и «сущностей стало больше» здесь соврало бы про Telegram.
    const before = authored === null ? 0 : authored.entities.length;
    const after = checked.canonical.entities.length;
    if (authored !== null && before !== after) {
      out.push({
        code: 'entities_renormalized',
        message: `Сущностей после канонизации ${String(after)} вместо ${String(before)}: соседние однотипные Telegram сливает, пересекающиеся разрезает.`,
      });
    }
    return out;
  }, [checked, authored]);

  const editable = EDITABLE.has(model.status);
  const dirty = source !== baseline;
  /**
   * Сохранённая версия без пометки, которую одобрение не пропустит (пункт 8 шапки): строка
   * словами и кнопка сохранения без правки. Ровно там, где экран ставит гейт утверждения
   * (`bannedOrgGate`), и только без правки — правку сохранит обычная кнопка, и разметит её тот
   * же сервер.
   */
  const unmarked = useMemo(
    () =>
      bannedOrgGate({ status: model.status, canonical }, bannedOrgs).length > 0
        ? unmarkedVersionText(canonical)
        : null,
    [model.status, canonical, bannedOrgs],
  );
  const resave = !dirty && unmarked !== null;
  // «Есть ли у поста картинка» — вопрос к дескриптору, а не к методу: подписи как отдельной
  // сущности не существует, а расширенным сообщением уходит и пост без картинки.
  const hasPicture = canonical.media !== null;
  const hasFile = document !== null;
  // Замороженное несёт выбранный метод; без заморозки (разметка не разобрана, отказ) метод
  // выводится тем же правилом — с файлом версии и политикой бренда.
  const measured: TransportInput =
    checked?.ok === true
      ? checked.canonical
      : { text: authored === null ? source : authored.text, media: canonical.media, document };
  const richReason = richReasonOf(measured, { richAlways: policy });
  const canSave = (dirty || resave) && parsed.ok && blocking.length === 0 && !busy;

  /**
   * Живое превью (решение Ильи 22.08). Наружу уходит РАЗОБРАННЫЙ, но не канонизированный
   * контент: превью обязано показывать последствия набранного, а не отредактированную за
   * человека форму. Пока разбор не удался, наружу идёт `null` — рисовать половину разметки
   * хуже, чем показать последнюю целую версию. Звёздочки и сноска сервера — последствие
   * набранного, а не правка за человека: у бренда с разметкой превью показывает их (пункт 8).
   */
  useEffect(() => {
    if (onTypedChange === undefined) return;
    onTypedChange(
      dirty && authored !== null ? { text: authored.text, entities: [...authored.entities] } : null,
    );
  }, [onTypedChange, dirty, authored]);

  const applyEdit = (edit: SourceEdit): void => {
    pendingSelection.current = edit.selection;
    setSource(edit.text);
    setNotice(null);
  };

  const selectionNow = (): SourceSelection => {
    const el = areaRef.current;
    if (el === null) return { start: source.length, end: source.length };
    return { start: el.selectionStart, end: el.selectionEnd };
  };

  const wrap = (marker: string): void => {
    const sel = selectionNow();
    applyEdit(wrapSelection(source, sel.start, sel.end, marker));
  };

  // Прямо по DOM, без pendingSelection: перерисовки здесь не будет — значение поля не
  // меняется, — а отложенное выделение осталось бы висеть и дёрнуло бы каретку при
  // следующем изменении текста.
  const caretTo = (offset: number): void => {
    const el = areaRef.current;
    if (el === null) return;
    el.focus();
    el.setSelectionRange(offset, offset);
  };

  const save = (): void => {
    if (!canSave || !parsed.ok) return;
    // Что сделает с этим текстом разметка сервера — на момент нажатия: подтверждение говорит о
    // сохранённом, а не о том, что наберут после.
    const marks = marksChange;
    setBusy(true);
    setError(null);
    setNotice(null);
    // Тела три поля. `content_hash` не отправляется (см. §4 в шапке), а
    // `link_preview_options` не отправляется: отсутствие ключа означает «карточка следует за
    // текстом» — бэкенд ставит её на первую ссылку сохранённого текста сам.
    //
    // `base_version_no` называет версию, ИЗ КОТОРОЙ сделана правка (K-008): пока карточка
    // открыта, соседняя вкладка могла сохранить свою версию, и бэкенд обязан отбить правку
    // поверх неё, а не переложить её молча на чужой текст.
    // `media` НЕ передаётся — значит наследуется (`listeners/ui.ts:636`): правка текста не
    // имеет права снимать картинку. Записано явно, потому что с приходом ключа `media`
    // «забыли передать» и «сняли картинку» стали неразличимы на глаз.
    saveVersion({
      draftId: model.draft_id,
      text: parsed.text,
      entities: parsed.entities,
      baseVersionNo: model.version_no,
    })
      .then((res) => {
        const created = res.version_no > model.version_no;
        setNotice({
          draftId: model.draft_id,
          text: created
            ? `Сохранено как v${String(res.version_no)}: она стала текущей, v${String(model.version_no)} ушла в «заменена».${marks === null ? '' : ` ${marksAfterSave(marks)}`}`
            : marks === null
              ? `Новой версии не появилось: после канонизации содержимое совпало с v${String(model.version_no)}. Это не ошибка — сервер не делает дубль.`
              : // Звёздочку или сноску стёрли, а сервер их вернул: «совпало после канонизации»
                // назвало бы не ту причину, и человек правил бы ещё раз.
                marksNoVersion(marks, model.version_no),
        });
        onChanged();
      })
      .catch((e: unknown) => {
        if (isStaleBaseVersion(e)) {
          // Подтверждение, а не ошибка: сообщение обязано пережить перезагрузку карточки
          // (`notice` переживает смену версии, `error` гасится ключом версии), а сама
          // карточка — показать ту версию, которая теперь текущая.
          setNotice({
            draftId: model.draft_id,
            text:
              `Пока шла правка, появилась версия новее v${String(model.version_no)}: её сохранил кто-то другой ` +
              'или соседняя вкладка. Колонка перезаполнена текущей версией, набранный текст не сохранён — ' +
              'внесите правку заново, иначе чужая работа была бы затёрта без следа.',
          });
          onChanged();
          return;
        }
        setError(saveErrorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const header = (
    <div className={styles['head']}>
      <span className="eyebrow">Исходник</span>
      <div className={styles['spacer']} />
      <span className="meta">
        {[
          'разметка',
          ...(hasPicture ? ['с картинкой'] : []),
          ...(hasFile ? ['с файлом'] : []),
        ].join(' · ')}{' '}
        · v{model.version_no}
      </span>
    </div>
  );

  if (!editable) {
    return (
      <div className="stack" style={{ gap: 10 }}>
        {header}
        <div className={`edarea ${styles['frozen']}`}>{baseline}</div>
        <LengthMeter canonical={canonical} />
        <div className={styles['note']}>
          <IconKey />
          <span>
            Версия в состоянии «{DRAFT_STATE_LABEL_RU[model.status]}» не правится: на её содержание
            уже выпущен ключ публикации либо её заменила следующая. Править можно только текущую
            версию в работе — снятие со слота возвращает эту возможность.
          </span>
        </div>
      </div>
    );
  }

  const toolButton = (
    label: string,
    title: string,
    onClick: () => void,
    style?: CSSProperties,
  ): JSX.Element => (
    <button
      type="button"
      className={`edbtn ${styles['tbtn']}`}
      title={title}
      aria-label={title}
      disabled={busy}
      onClick={onClick}
      {...(style === undefined ? {} : { style })}
    >
      {label}
    </button>
  );

  const iconButton = (icon: JSX.Element, title: string, onClick: () => void): JSX.Element => (
    <button
      type="button"
      className={`edbtn ${styles['tbtn']}`}
      title={title}
      aria-label={title}
      disabled={busy}
      onClick={onClick}
    >
      {icon}
    </button>
  );

  return (
    <div className="stack" style={{ gap: 10 }}>
      {header}

      <div>
        <div className="edbar" role="toolbar" aria-label="Разметка">
          {toolButton('B', 'Жирный (Ctrl+B)', () => {
            wrap(MARKERS.bold);
          })}
          {toolButton(
            'I',
            'Курсив (Ctrl+I)',
            () => {
              wrap(MARKERS.italic);
            },
            { fontStyle: 'italic' },
          )}
          {toolButton(
            'U',
            'Подчёркнутый',
            () => {
              wrap(MARKERS.underline);
            },
            { textDecoration: 'underline' },
          )}
          {toolButton(
            'S',
            'Зачёркнутый',
            () => {
              wrap(MARKERS.strikethrough);
            },
            { textDecoration: 'line-through' },
          )}
          <span className="edsep" />
          {iconButton(<IconEyeOff />, 'Спойлер', () => {
            wrap(MARKERS.spoiler);
          })}
          {iconButton(<IconCode />, 'Код', () => {
            wrap(MARKERS.code);
          })}
          {iconButton(<IconQuote />, 'Цитата', () => {
            const sel = selectionNow();
            applyEdit(toggleQuote(source, sel.start, sel.end));
          })}
          {iconButton(<IconLink />, 'Ссылка', () => {
            const sel = selectionNow();
            applyEdit(wrapLink(source, sel.start, sel.end));
          })}
        </div>

        <textarea
          ref={areaRef}
          className={`edarea ${styles['area']}`}
          aria-label="Исходник поста в разметке"
          spellCheck={false}
          value={source}
          disabled={busy}
          onChange={(e) => {
            setSource(e.target.value);
            setNotice(null);
          }}
          onKeyDown={(e) => {
            if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
            // Сначала физическая клавиша, потом символ: пост пишут по-русски, и в русской
            // раскладке Ctrl+B приходит как `key: 'и'`. По одному `key` горячая клавиша
            // работала бы ровно до первого переключения языка.
            const k = e.key.toLowerCase();
            const bold = e.code === 'KeyB' || k === 'b';
            const italic = e.code === 'KeyI' || k === 'i';
            if (!bold && !italic) return;
            e.preventDefault();
            wrap(bold ? MARKERS.bold : MARKERS.italic);
          }}
        />
      </div>

      <div className={styles['meterRow']}>
        <LengthMeter canonical={measured} />
        {/* Почему пост уйдёт так, а не иначе, — объяснение, и оно в ⓘ (правило партии §1): сам
            вывод рядом уже стоит данными — в счётчике и в полосе карточки превью справа. */}
        <InfoPopover label="Каким сообщением уйдёт пост">
          <span>
            {richReason === 'media'
              ? `У поста есть картинка, поэтому он уйдёт расширенным сообщением: картинка первым блоком, карточки превью ссылки не будет. Потолок — ${group(REQUEST_BYTE_CAP)} байт UTF-8, для русского текста это около 16 000 знаков.`
              : richReason === 'document'
                ? 'К посту приложен файл, поэтому он уйдёт расширенным сообщением: файл последним блоком, карточки превью ссылки не будет.'
                : richReason === 'length'
                  ? `Текст перерос порог ${SEND_MESSAGE_THRESHOLD} кодпоинтов — пост уйдёт расширенным сообщением и останется без карточки превью ссылки. Потолок — ${group(REQUEST_BYTE_CAP)} байт UTF-8, для русского текста это около 16 000 знаков.`
                  : richReason === 'policy'
                    ? 'Все посты бренда уходят расширенным сообщением (Настройки): карточки превью ссылки не будет.'
                    : `Порог обычного сообщения — ${SEND_MESSAGE_THRESHOLD} кодпоинтов: до него пост уходит с карточкой превью снизу, выше — расширенным сообщением, и карточки у него уже не будет.`}
          </span>
        </InfoPopover>
      </div>
      {overLimit(measured) || !parsed.ok ? (
        <div className="micro">
          {[
            overLimit(measured) ? 'Лимит превышен, сохранение выключено.' : null,
            parsed.ok
              ? null
              : 'Пока разметка не разобрана, счётчик считает исходник вместе с маркерами.',
          ]
            .filter((x) => x !== null)
            .join(' ')}
        </div>
      ) : null}

      {parsed.ok ? (
        <span className={styles['note']} style={{ color: 'var(--green-ink)' }}>
          <IconCheck /> Разметка корректна · сущностей: {parsed.entities.length}
        </span>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {parsed.issues.map((i) => (
            <div className="val val-err" key={`${i.code}:${String(i.offset)}`}>
              <IconWarn />
              <span>
                <button
                  type="button"
                  className={styles['pos']}
                  onClick={() => {
                    caretTo(i.offset);
                  }}
                >
                  строка {i.line}, колонка {i.column}
                </button>
                {i.message}
              </span>
            </div>
          ))}
          <div className="micro">
            Сохранение выключено, пока разметка не разобрана: отправить в канал можно только то, что
            разложено на текст и сущности.
          </div>
        </div>
      )}

      {blocking.map((i) => (
        <div className="val val-err" key={`${i.level}:${i.code}`}>
          <IconWarn />
          <span>{i.message}</span>
        </div>
      ))}

      {changes.length === 0 ? (
        <div className={styles['note']}>
          <IconChevronRight />
          <span>Что изменит Telegram в набранном тексте — ничего.</span>
        </div>
      ) : (
        <div className={styles['changes']}>
          <span className="eyebrow">Что изменит Telegram в набранном тексте</span>
          {changes.map((c) => (
            <div className="val val-warn" key={c.code}>
              <IconWarn />
              <span>
                {c.message} <span className="meta">{c.code}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Что сделает разметка сервера с набранным — данные, а не объяснение: человек должен
          знать до нажатия, что в посте появятся звёздочки и сноска, которых нет в его поле. */}
      {dirty && marksChange !== null ? (
        <div className="val val-info" data-testid="banned-org-marks">
          <span>{marksBeforeSave(marksChange)}</span>
        </div>
      ) : null}

      {resave ? (
        <div className="val val-warn" data-testid="banned-org-unmarked">
          <IconWarn />
          <span>{unmarked}</span>
        </div>
      ) : null}

      {dirty ? (
        <div className="val val-info">
          <span>
            Правка не сохранена: утверждение и тестовая отправка идут по v{model.version_no}, пока
            вы её не сохраните.
          </span>
        </div>
      ) : null}

      {error === null ? null : (
        <div className="val val-err">
          <IconWarn />
          <span>{error}</span>
        </div>
      )}

      {notice === null ? null : (
        <div className="val val-info">
          <span>{notice.text}</span>
        </div>
      )}

      <div className={styles['saveRow']}>
        {dirty ? (
          <>
            <button
              type="button"
              className={canSave ? 'btn btn-pri btn-sm' : 'btn btn-sm btn-dis'}
              disabled={!canSave}
              onClick={save}
            >
              Сохранить как v{model.version_no + 1}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => {
                setSource(baseline);
                setNotice(null);
                setError(null);
              }}
            >
              Отменить правку
            </button>
            <span className="micro">
              кнопка не перезаписывает v{model.version_no}: она создаёт версию v
              {model.version_no + 1}, а текущая уходит в «заменена»
            </span>
          </>
        ) : resave ? (
          // Версия до разметки: без правки сохранение тоже родит новую версию — с пометками.
          <button
            type="button"
            className={canSave ? 'btn btn-pri btn-sm' : 'btn btn-sm btn-dis'}
            disabled={!canSave}
            onClick={save}
          >
            Сохранить с пометками как v{model.version_no + 1}
          </button>
        ) : (
          <span className="micro">
            кнопка сохранения появится при правке: правка = новая версия, а не перезапись
          </span>
        )}
      </div>
    </div>
  );
}
